import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle, ChevronDown, ChevronRight, Languages, Lightbulb,
  Loader2, RotateCcw, Save, Sparkles, Wand2,
} from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { api, COPY_FIELDS, type CopyPrompt } from '@/lib/api';
import { ETIQUETAS_CAMPO, AYUDA_CAMPO } from '@/components/copy/tipos';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';

/**
 * Prompts de la IA de Copy: port del modal de configuración de la app antigua
 * (copy.tusereno.es), aquí como pestaña propia porque son 19 textos largos y en
 * un modal no se leían.
 *
 * Dos diferencias deliberadas con el modal viejo:
 *  1. `system_prompt` ya no está. Gobernaba la generación de los catorce campos
 *     de una tacada, que no se ha portado —el editor genera campo a campo—, así
 *     que no lo dispara nada. En la app vieja tampoco: era el único prompt que
 *     no pasaba por el resolutor de overrides, o sea que se podía editar por la
 *     API y no se ejecutaba nunca. Aquí solo se listan prompts que se usan.
 *  2. La traducción son dos claves de verdad. En la app vieja había una sola
 *     (`translation_system_prompt`) cuyo texto por defecto en el editor no era
 *     el que se ejecutaba, y el override pisaba a la vez traducir-un-campo y
 *     traducir-el-lote — con lo que el lote perdía su regla del título de 50
 *     caracteres. Ahora cada una tiene su default real y su editor.
 */

interface MetaPrompt {
  clave: string;
  titulo: string;
  icono: LucideIcon;
  descripcion: string;
}

/**
 * Los cuatro prompts de sistema, en el orden en que se disparan al trabajar:
 * primero se escribe, luego se traduce, y los títulos van aparte.
 */
const PROMPTS_SISTEMA: MetaPrompt[] = [
  {
    clave: 'single_field_system_prompt',
    titulo: 'Generar un campo',
    icono: Wand2,
    descripcion:
      'Se usa cada vez que pides a la IA que escriba un campo desde el editor. Fija el tono y las reglas que valen para todos los campos (destacar el aire acondicionado, ofrecer cuna bajo petición, no revelar nunca la dirección exacta) y se combina con las instrucciones específicas del campo que estés generando.',
  },
  {
    clave: 'translate_field_system_prompt',
    titulo: 'Traducir un campo a varios idiomas',
    icono: Languages,
    descripcion:
      'Se usa al traducir un campo suelto: coge el texto del idioma de origen y lo devuelve en todos los idiomas que hayas marcado, de una tacada.',
  },
  {
    clave: 'translate_batch_system_prompt',
    titulo: 'Traducir todos los campos a un idioma',
    icono: Languages,
    descripcion:
      'Se usa al volcar el anuncio entero a un idioma destino. Lleva una regla que el anterior no tiene: el título no puede pasar de 50 caracteres. Si la quitas de aquí, las traducciones en bloque empezarán a devolver títulos que Hostify recorta.',
  },
  {
    clave: 'title_generator_system_prompt',
    titulo: 'Idear títulos',
    icono: Lightbulb,
    descripcion:
      'Se usa al pedir propuestas de título: devuelve 5 opciones distintas de 50 caracteres como máximo, mirando las fotos y la ubicación del anuncio.',
  },
];

const CLAVES_CAMPO = COPY_FIELDS.map((campo) => `field_instructions_${campo}`);

/** Texto del aviso posterior al guardado, con singulares y plurales cuadrados. */
function resumenGuardado(personalizados: number, restaurados: number): string {
  const total = personalizados + restaurados;
  const partes: string[] = [];
  if (personalizados) {
    partes.push(`${personalizados} ${personalizados === 1 ? 'personalizado' : 'personalizados'}`);
  }
  if (restaurados) {
    partes.push(
      `${restaurados} ${restaurados === 1 ? 'restaurado' : 'restaurados'} al valor por defecto`
    );
  }
  return `${total === 1 ? 'Guardado 1 prompt' : `Guardados ${total} prompts`}: ${partes.join(' y ')}.`;
}

export default function CopyPromptsPage() {
  const { data: prompts, loading, error: errorCarga, refresh } = useApi<Record<string, CopyPrompt>>(
    () => api.getCopyPrompts(),
    []
  );

  const [borrador, setBorrador] = useState<Record<string, string>>({});
  const [camposAbiertos, setCamposAbiertos] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errorGuardado, setErrorGuardado] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // El borrador se rehace con cada respuesta del servidor, también tras guardar:
  // así los badges y el estado "sin cambios" salen de lo que hay guardado de
  // verdad y no de lo que creíamos haber mandado.
  useEffect(() => {
    if (!prompts) return;
    const inicial: Record<string, string> = {};
    for (const [clave, info] of Object.entries(prompts)) inicial[clave] = info.value ?? '';
    setBorrador(inicial);
  }, [prompts]);

  const defaultDe = (clave: string) => (prompts?.[clave]?.default ?? '').trim();
  const valorDe = (clave: string) => borrador[clave] ?? '';
  const esPersonalizado = (clave: string) => {
    const actual = valorDe(clave).trim();
    return actual !== '' && actual !== defaultDe(clave);
  };

  /**
   * Qué mandamos al servidor.
   *
   * Un prompt vacío o idéntico al default significa "usa el default", y eso se
   * expresa mandando string vacío: el servidor borra el override. Solo van las
   * claves cuyo estado deseado difiere del guardado —el endpoint hace guardado
   * parcial a propósito, así que mandar las 19 cada vez pisaría lo que otra
   * pestaña acabe de cambiar.
   */
  const cambios = useMemo(() => {
    const payload: Record<string, string> = {};
    let personalizados = 0;
    let restaurados = 0;
    // Sin borrador sembrado no hay nada que comparar; sin esta guarda, el primer
    // render calcularía "resetear todo".
    if (prompts && Object.keys(borrador).length > 0) {
      for (const [clave, info] of Object.entries(prompts)) {
        const actual = (borrador[clave] ?? '').trim();
        const deseado = actual && actual !== info.default.trim() ? actual : '';
        const guardado = info.is_custom ? info.value.trim() : '';
        if (deseado === guardado) continue;
        payload[clave] = deseado;
        if (deseado) personalizados++;
        else restaurados++;
      }
    }
    return { payload, personalizados, restaurados, total: Object.keys(payload).length };
  }, [prompts, borrador]);

  const totalPersonalizados = useMemo(
    () => Object.keys(prompts ?? {}).filter((clave) => esPersonalizado(clave)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prompts, borrador]
  );

  /**
   * "Sin guardar" es estar dentro del payload, no que el texto difiera del
   * guardado: si alguien vacía un prompt que ya estaba en su valor por defecto,
   * el texto cambia pero no hay nada que mandar, y marcarlo mentiría sobre el
   * contador del pie.
   */
  const sinGuardar = (clave: string) => clave in cambios.payload;

  const escribir = (clave: string, valor: string) => {
    setBorrador((b) => ({ ...b, [clave]: valor }));
    setAviso(null);
  };

  const restaurar = (clave: string) => escribir(clave, prompts?.[clave]?.default ?? '');

  const descartar = () => {
    if (!prompts) return;
    const inicial: Record<string, string> = {};
    for (const [clave, info] of Object.entries(prompts)) inicial[clave] = info.value ?? '';
    setBorrador(inicial);
    setAviso(null);
    setErrorGuardado(null);
  };

  const guardar = async () => {
    if (!cambios.total) return;
    setGuardando(true);
    setErrorGuardado(null);
    setAviso(null);
    try {
      await api.saveCopyPrompts(cambios.payload);
      setAviso(resumenGuardado(cambios.personalizados, cambios.restaurados));
      refresh();
    } catch (e: any) {
      setErrorGuardado(e?.message || 'No se pudieron guardar los prompts');
    } finally {
      setGuardando(false);
    }
  };

  // El spinner solo en la primera carga: al refrescar tras guardar, cambiar la
  // página entera por un spinner tiraría el scroll y el aviso recién puesto.
  if (loading && !prompts) return <LoadingSpinner />;

  if (errorCarga && !prompts) {
    return (
      <EmptyState
        title="No se pudieron cargar los prompts"
        description={errorCarga}
        action={<button onClick={refresh} className="btn-primary">Reintentar</button>}
      />
    );
  }

  const claveDisponible = (clave: string) => !!prompts && clave in prompts;
  const sistema = PROMPTS_SISTEMA.filter((p) => claveDisponible(p.clave));
  const campos = COPY_FIELDS.filter((campo) => claveDisponible(`field_instructions_${campo}`));
  const camposPersonalizados = CLAVES_CAMPO.filter(
    (clave) => claveDisponible(clave) && esPersonalizado(clave)
  ).length;

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex gap-3">
        <AlertTriangle size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <p className="font-medium">Estos prompts los comparten el CRM y la app antigua.</p>
          <p className="mt-1">
            Mientras copy.tusereno.es siga en pie, ambos leen el mismo almacén: lo que cambies aquí
            afecta también a lo que genere la app vieja. Edítalos <strong>solo desde esta pantalla</strong>,
            que es la única que distingue los dos prompts de traducción.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-semibold text-sereno-dark flex items-center gap-2">
              <Sparkles size={18} className="text-sereno-dark/50" />
              Instrucciones que sigue la IA
            </h2>
            <p className="text-sm text-sereno-dark/50 mt-1 max-w-2xl">
              Cada prompt trae un texto por defecto. Si lo editas, se guarda como personalizado y
              manda sobre el default. Vacíalo —o pulsa Restaurar y guarda— para volver al original.
            </p>
            <p className="text-xs text-sereno-dark/40 mt-2">
              {totalPersonalizados === 0
                ? 'Ningún prompt personalizado: todo funciona con los textos por defecto.'
                : `${totalPersonalizados} de ${Object.keys(prompts ?? {}).length} prompts personalizados.`}
              {cambios.total > 0 && ` · ${cambios.total} sin guardar.`}
            </p>
          </div>
          {/* En móvil ocupan el ancho y llegan a 44px de alto: son la acción
              principal de la pantalla y quedaban en 36px, por debajo de lo que
              necesita un dedo. */}
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 sm:flex-shrink-0">
            {cambios.total > 0 && (
              <button
                onClick={descartar}
                className="btn-ghost text-sm w-full sm:w-auto min-h-[2.75rem] sm:min-h-0"
                disabled={guardando}
              >
                Descartar cambios
              </button>
            )}
            <button
              onClick={guardar}
              disabled={guardando || cambios.total === 0}
              className="btn-primary flex items-center justify-center gap-2 text-sm w-full sm:w-auto min-h-[2.75rem] sm:min-h-0"
            >
              {guardando ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {guardando
                ? 'Guardando…'
                : cambios.total === 0
                  ? 'Sin cambios'
                  : `Guardar ${cambios.total} ${cambios.total === 1 ? 'cambio' : 'cambios'}`}
            </button>
          </div>
        </div>

        {errorGuardado && (
          <div className="mt-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">
            {errorGuardado}
          </div>
        )}
        {aviso && (
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm px-3 py-2">
            {aviso}
          </div>
        )}
      </div>

      {/* ── Prompts de sistema ─────────────────────────────────────────── */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-sereno-dark/70 uppercase tracking-wide">
          Prompts de sistema
        </h3>

        {sistema.map((meta) => (
          <TarjetaPrompt
            key={meta.clave}
            clave={meta.clave}
            titulo={meta.titulo}
            icono={meta.icono}
            descripcion={meta.descripcion}
            valor={valorDe(meta.clave)}
            filas={12}
            personalizado={esPersonalizado(meta.clave)}
            sinGuardar={sinGuardar(meta.clave)}
            onChange={(v) => escribir(meta.clave, v)}
            onRestaurar={() => restaurar(meta.clave)}
            extra={
              meta.clave === 'translate_field_system_prompt' ? (
                <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-xs px-3 py-2 mt-3">
                  <strong>Hay dos prompts de traducción y los dos se ejecutan.</strong> Este es el de
                  traducir un campo a varios idiomas. El siguiente es el de traducir el anuncio
                  entero a un idioma, y lleva la regla del título de 50 caracteres. En la app antigua
                  eran una sola clave y el editor enseñaba un texto que en realidad nunca se usaba;
                  si personalizas uno, personaliza también el otro o quedarán descompensados.
                </div>
              ) : undefined
            }
          />
        ))}
      </div>

      {/* ── Instrucciones por campo (plegables) ────────────────────────── */}
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setCamposAbiertos((v) => !v)}
          className="card w-full text-left flex items-start gap-3 hover:bg-sereno-blue/10 transition-colors"
        >
          {camposAbiertos ? (
            <ChevronDown size={18} className="text-sereno-dark/50 flex-shrink-0 mt-0.5" />
          ) : (
            <ChevronRight size={18} className="text-sereno-dark/50 flex-shrink-0 mt-0.5" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sereno-dark">
                Instrucciones específicas por campo
              </span>
              <span className="badge bg-gray-100 text-gray-500">{campos.length}</span>
              {camposPersonalizados > 0 && (
                <span className="badge bg-purple-100 text-purple-700">
                  {camposPersonalizados} personalizadas
                </span>
              )}
            </div>
            <p className="text-sm text-sereno-dark/50 mt-1">
              Se añaden al prompt «Generar un campo» según el campo que estés escribiendo. No
              intervienen al traducir.
            </p>
          </div>
        </button>

        {camposAbiertos &&
          campos.map((campo) => (
            <TarjetaPrompt
              key={campo}
              clave={`field_instructions_${campo}`}
              titulo={`Instrucciones para «${ETIQUETAS_CAMPO[campo]}»`}
              descripcion={AYUDA_CAMPO[campo]}
              valor={valorDe(`field_instructions_${campo}`)}
              filas={6}
              personalizado={esPersonalizado(`field_instructions_${campo}`)}
              sinGuardar={sinGuardar(`field_instructions_${campo}`)}
              onChange={(v) => escribir(`field_instructions_${campo}`, v)}
              onRestaurar={() => restaurar(`field_instructions_${campo}`)}
            />
          ))}
      </div>

      {/* Repetimos el botón al pie: con 19 editores largos, el de arriba queda a
          varias pantallas de distancia del último cambio. */}
      <div className="card flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-sereno-dark/50">
          {cambios.total === 0
            ? 'No hay cambios pendientes.'
            : `${cambios.total} ${cambios.total === 1 ? 'prompt modificado' : 'prompts modificados'} sin guardar.`}
        </p>
        <div className="flex items-center gap-2">
          {cambios.total > 0 && (
            <button onClick={descartar} className="btn-ghost text-sm" disabled={guardando}>
              Descartar cambios
            </button>
          )}
          <button
            onClick={guardar}
            disabled={guardando || cambios.total === 0}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            {guardando ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {guardando ? 'Guardando…' : 'Guardar prompts'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface TarjetaPromptProps {
  clave: string;
  titulo: string;
  descripcion: string;
  icono?: LucideIcon;
  valor: string;
  filas: number;
  personalizado: boolean;
  sinGuardar: boolean;
  onChange: (valor: string) => void;
  onRestaurar: () => void;
  extra?: React.ReactNode;
}

function TarjetaPrompt({
  clave, titulo, descripcion, icono: Icono, valor, filas,
  personalizado, sinGuardar, onChange, onRestaurar, extra,
}: TarjetaPromptProps) {
  return (
    <div className="card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {Icono && <Icono size={16} className="text-sereno-dark/50 flex-shrink-0" />}
            <span className="font-semibold text-sereno-dark">{titulo}</span>
            {personalizado && (
              <span className="badge bg-purple-100 text-purple-700">Personalizado</span>
            )}
            {sinGuardar && <span className="badge bg-amber-100 text-amber-800">Sin guardar</span>}
          </div>
          <p className="text-sm text-sereno-dark/50 mt-1">{descripcion}</p>
          <code className="inline-block font-mono text-xs bg-gray-100 text-sereno-dark/60 rounded px-2 py-0.5 mt-2">
            {clave}
          </code>
        </div>
        <button
          type="button"
          onClick={onRestaurar}
          disabled={!personalizado}
          title="Repone el texto por defecto en el editor (no se guarda hasta que pulses Guardar)"
          className="btn-ghost text-xs flex items-center gap-1 flex-shrink-0 min-h-[2.75rem] sm:min-h-0 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <RotateCcw size={14} /> Restaurar
        </button>
      </div>

      {extra}

      <AreaCrece valor={valor} filas={filas} onChange={onChange} />

      <p className="text-xs text-sereno-dark/40 mt-1">
        Déjalo vacío o igual al texto por defecto para que deje de contar como personalizado.
      </p>
    </div>
  );
}

interface AreaCreceProps {
  valor: string;
  filas: number;
  onChange: (valor: string) => void;
}

/**
 * Textarea que crece con el contenido.
 *
 * Los prompts van de 10 a 50 líneas y llevan listas numeradas y un bloque JSON:
 * con una caja de altura fija se editan a ciegas. `rows` sigue marcando el
 * mínimo porque al poner la altura en `auto` el navegador vuelve a él antes de
 * medir. Monoespaciada para que la indentación del JSON se lea como se envía.
 */
function AreaCrece({ valor, filas, onChange }: AreaCreceProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [valor]);

  return (
    <textarea
      ref={ref}
      rows={filas}
      value={valor}
      spellCheck={false}
      onChange={(e) => onChange(e.target.value)}
      className="input mt-3 font-mono text-xs leading-relaxed resize-none overflow-hidden"
      placeholder="Vacío = se usa el texto por defecto"
    />
  );
}
